import type {
  ValidationRule,
  ValidationRuleGenerator
} from "../scenarioValidator";
import { CORRELATIONS_FIELDS, CORRELATION_LIMITS } from "../validatorConstants";
import { correlationsAtIntakeChecks } from "./correlationsAtIntakeChecks";
import { correlationsAtMotorChecks } from "./correlationsAtMotorChecks";
import { correlationsAtPumpDischargeChecks } from "./correlationsAtPumpDischargeChecks";
import { correlationsAtPumpIntakeChecks } from "./correlationsAtPumpIntakeChecks";
import { correlationsAtSensorChecks } from "./correlationsAtSensorChecks";
import { correlationsAtSurfaceChecks } from "./correlationsAtSurfaceChecks";

export type CorrelationFieldName =
  (typeof CORRELATIONS_FIELDS)[keyof typeof CORRELATIONS_FIELDS][number];

export const correlationCheckStageNames = [
  "motor",
  "pump_discharge",
  "pump_intake",
  "sensor",
  "surface",
  "intake"
] as const;

export const corrAbbrevs = {
  oil_gravity: "API",
  gas_specific_gravity: "Gas SG",
  temperature: "Temp",
  pressure: "Press",
  oil_solution_gas: "RSO",
  bubblepoint_pressure: "Pb",
  oil_formation_volume_factor: "Bo",
  dead_oil_viscosity: "u_od",
  live_oil_viscosity: "u_o",
  pseudo_reduced_pressure: "Ppr",
  pseudo_reduced_temperature: "Tpr"
} as const;

export type StageNames = (typeof correlationCheckStageNames)[number];

export type CorrelationChecksNames =
  `${CorrelationFieldName}_${keyof typeof corrAbbrevs}_${StageNames}`;

export type CorrelationValidationGenerator = (
  correlationLimits: Record<string, number>,
  correlationFieldName: CorrelationFieldName,
  args: Parameters<ValidationRuleGenerator>[0]
) => ValidationRule[];

// Correlation Validation source checks:
// static/Correlation_Limit_Schtuff.xlsx
export const correlationChecks: ValidationRuleGenerator = args =>
  Object.entries(CORRELATIONS_FIELDS).flatMap(
    ([correlationType, correlationFieldNames]) =>
      correlationFieldNames.flatMap(correlationFieldName => {
        const correlationFieldValue =
          args.correlations.value?.[correlationFieldName];
        if (!correlationFieldValue) return [];

        const correlationsLimits =
          CORRELATION_LIMITS[
            correlationType as keyof typeof CORRELATION_LIMITS
          ];
        if (!correlationsLimits) return [];

        const correlationLimits =
          correlationsLimits[
            correlationFieldValue as keyof typeof correlationsLimits
          ];
        if (!correlationLimits) return [];

        return [
          correlationsAtIntakeChecks,
          correlationsAtMotorChecks,
          correlationsAtPumpDischargeChecks,
          correlationsAtPumpIntakeChecks,
          correlationsAtSensorChecks,
          correlationsAtSurfaceChecks
        ].flatMap(fn => fn(correlationLimits, correlationFieldName, args));
      })
  );
